import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import DownloadIcon from "@mui/icons-material/Download";

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import StudyDropDown from "../../common/components/StudyDropDown";
import AgendaDialog from "./components/AgendaDialog";
import { useGetAgendasQuery } from "../../api/useGetAgendas";
import { startOfWeek, add, format, compareAsc } from "date-fns";
import { useUpdateAgendaQuery } from "./../../api/useUpdateAgendas";
import { useResetAgendaQuery } from "./../../api/useResetAgenda";
import { useDeleteAgendaQuery } from "./../../api/useDeleteAgenda";
import SubmissionLinkEditor from "./components/SubmissionLinkEditor";
import { groupByWeek, parseISODate } from "../../utils/date";

const handleEditClick = (agenda) => {};

const EditableTableCell = ({ rowId, value, name, onChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  if (name === "submissionLinks") {
    return (
      <TableCell>
        <SubmissionLinkEditor rowId={rowId} value={value} onChange={onChange} />
      </TableCell>
    );
  }

  if (name === "note") {
    return (
      <TableCell
        onClick={() => setIsEditing(true)}
        sx={{
          minHeight: "120px",
          maxHeight: "200px",
          verticalAlign: "top",
          padding: "16px",
        }}
      >
        {isEditing ? (
          <TextField
            size="small"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={() => {
              onChange(rowId, name, localValue);
              setIsEditing(false);
            }}
            autoFocus
            fullWidth
            multiline
            minRows={5}
            maxRows={8}
            sx={{
              "& .MuiInputBase-root": {
                height: "auto",
                minHeight: "120px",
                alignItems: "flex-start",
              },
              "& .MuiInputBase-input": {
                whiteSpace: "pre-wrap",
                overflowY: "auto",
                maxHeight: "180px",
                lineHeight: "1.5",
                padding: "8px",
              },
            }}
          />
        ) : (
          <div
            style={{
              whiteSpace: "pre-wrap",
              overflowY: "auto",
              maxHeight: "180px",
              minHeight: "120px",
              lineHeight: "1.5",
            }}
          >
            {value}
          </div>
        )}
      </TableCell>
    );
  }

  return (
    <TableCell onClick={() => setIsEditing(true)}>
      {isEditing ? (
        <TextField
          size="small"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={() => {
            onChange(rowId, name, localValue);
            setIsEditing(false);
          }}
          autoFocus
          fullWidth
        />
      ) : (
        value
      )}
    </TableCell>
  );
};
const WeekTable = ({ week, data, onCellChange, onDeleteClick, key }) => {
  return (
    <TableContainer component={Paper} sx={{ marginBottom: 4 }}>
      <Table sx={{ minWidth: 650 }} aria-label="weekly schedule table">
        <TableHead>
          <TableRow>
            <TableCell>날짜</TableCell>
            <TableCell>요일</TableCell>
            <TableCell>강의</TableCell>
            <TableCell>과제</TableCell>
            <TableCell>레벨</TableCell>
            <TableCell>제출 링크</TableCell>
            <TableCell>노트</TableCell>
            <TableCell>삭제</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((agenda, index) => (
            <TableRow key={index}>
              <EditableTableCell
                rowId={agenda._id}
                value={format(
                  parseISODate(agenda.closeDate.split("T")[0]),
                  "yyyy.MM.dd"
                )}
                name="closeDate"
                onChange={onCellChange}
              />
              <TableCell>
                {format(parseISODate(agenda.closeDate.split("T")[0]), "EEEE")}
              </TableCell>
              <EditableTableCell
                rowId={agenda._id}
                value={agenda.lecture}
                name="lecture"
                onChange={onCellChange}
              />
              <EditableTableCell
                rowId={agenda._id}
                value={agenda.assignment}
                name="assignment"
                onChange={onCellChange}
              />
              <EditableTableCell
                rowId={agenda._id}
                value={agenda.level}
                name="level"
                onChange={onCellChange}
              />
              <EditableTableCell
                rowId={agenda._id}
                value={agenda.submissionLinks}
                name="submissionLinks"
                onChange={onCellChange}
              />
              <EditableTableCell
                rowId={agenda._id}
                value={agenda.note}
                name="note"
                onChange={onCellChange}
              />

              <TableCell>
                <Button
                  size="small"
                  color="error"
                  onClick={() => onDeleteClick(agenda._id)}
                >
                  삭제
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const AdminAgendaPage = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedStudy, setSelectedStudy] = React.useState(null);
  const [open, setOpen] = useState(false);
  const [edits, setEdits] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const queryClient = useQueryClient();
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
  };
  const {
    data: agendas,
    isLoading,
    isError,
    error,
  } = useGetAgendasQuery({
    studyId: selectedStudy?._id,
  });

  const {
    mutate: updateAgenda,
    isPending: isUpdateLoading,
    isError: isUpdateError,
    error: updateError,
  } = useUpdateAgendaQuery();

  const {
    mutate: resetAgenda,
    isError: isResetError,
    error: resetError,
  } = useResetAgendaQuery();

  const {
    mutate: deleteAgenda,
    isPending: isDeleteLoading,
    isError: isDeleteError,
    error: deleteError,
  } = useDeleteAgendaQuery();

  const handleCellChange = (id, field, value) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const handleDeleteClick = (agendaId) => {
    if (window.confirm("정말 삭제하시겠습니까?")) {
      deleteAgenda(agendaId, {
        onSuccess: () => {
          queryClient.invalidateQueries(["agendas", selectedStudy._id]);
        },
        onError: (error) => {
          setErrorMessage(
            error.response?.data?.error || "삭제에 실패했습니다."
          );
        },
      });
    }
  };

  const weeks = groupByWeek(agendas || []);

  const handleStudyChange = (event) => {
    setSelectedStudy(event.target.value);
  };
  const handleSubmitEdits = () => {
    // 여기서 edits 상태를 사용하여 변경 사항을 백엔드에 전송
    const data = Object.keys(edits).map((id) => {
      return { ...edits[id], id: id };
    });
    // 백엔드 요청 로직 추가
    updateAgenda(data, {
      onSuccess: () => {
        queryClient.invalidateQueries(["agendas", selectedStudy._id]);
        setEdits({});
        // setRefreshKey((prevKey) => prevKey + 1);
      },
      onError: (error) => {
        setErrorMessage(error.response.data.error || "수정에 실패했습니다."); // 실패 시 에러 메시지 설정
      },
    });
  };

  const handleReset = () => {
    if (!selectedStudy) {
      setErrorMessage("리셋할 스터디를 선택해주세요");
      return;
    }
    setErrorMessage("");
    setResetConfirmOpen(true);
  };

  const handleResetCancel = () => {
    setResetConfirmOpen(false);
  };

  const handleResetConfirm = () => {
    setResetConfirmOpen(false);
    resetAgenda(selectedStudy._id, {
      onSuccess: () => {
        queryClient.invalidateQueries(["agendas", selectedStudy._id]);
      },
      onError: (error) => {
        setErrorMessage(error.response.data.message || "삭제에 실패했습니다."); // 실패 시 에러 메시지 설정
      },
    });
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/template.xlsx"; // public 폴더 내의 파일 경로
    link.download = "template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Box sx={{ width: "100%", padding: 2 }}>
        <Box container spacing={2} alignItems="center">
          <StudyDropDown
            selectedStudy={selectedStudy}
            handleStudyChange={handleStudyChange}
          />
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mt: 2,
              mb: 2,
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="contained" onClick={handleOpen}>
                새로 만들기
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitEdits}
                disabled={isUpdateLoading}
              >
                {isUpdateLoading ? "수정중" : "수정하기"}
              </Button>
              <Button variant="contained" onClick={handleReset}>
                리셋하기
              </Button>
            </Box>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
            >
              템플릿 다운로드
            </Button>
          </Box>
          {(isUpdateError || errorMessage || isResetError) && (
            <Alert severity="error">{errorMessage}</Alert>
          )}
          {agendas?.length === 0 || !agendas ? (
            <div>데이터 없음</div>
          ) : (
            <Box item>
              {Object.entries(weeks).map(([weekStart, weekData], index) => (
                <Box key={weekStart}>
                  <Typography variant="h6" gutterBottom>
                    {"Week " + (index + 1) + " (" + weekStart + ")"}
                  </Typography>
                  <WeekTable
                    key={refreshKey}
                    week={weekStart}
                    data={weekData}
                    onCellChange={handleCellChange}
                    onDeleteClick={handleDeleteClick}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      <AgendaDialog open={open} closeModal={handleClose} />
      <Dialog open={resetConfirmOpen} onClose={handleResetCancel}>
        <DialogTitle>리셋 확인</DialogTitle>
        <DialogContent>
          <DialogContentText>리셋하시겠습니까?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetCancel}>아니오</Button>
          <Button onClick={handleResetConfirm} variant="contained" autoFocus>
            네
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminAgendaPage;
